// @flow
import React, { useEffect, useState, useRef } from 'react';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import { useTheme } from '@mui/material/styles';
import { createClasses } from '../../../utils/stylesUtils';
import { auth } from '../../../services/firebase';
import { useProcessImportMutation, useGetImportStatusQuery } from '../../../features/api/tenantsApi';
import useInterval from '../../../hooks/useInterval';
import { formatNumber } from '../csvTemplateUtils';
import processingViewStyles from './ProcessingView.Style';

type ImportStatus = {
  status: 'processing' | 'completed' | 'failed',
  totalRows: number,
  processedRows: number,
  created: number,
  skipped: number,
  failed: number,
  errors: Array<{row: number, phone: string, reason: string}>,
  completedAt?: string
};

type Props = {
  jobId: string,
  columnMapping: {[string]: string},
  onProcessingComplete: (status: ImportStatus) => void,
  onError: (error: string) => void
};

const POLL_INTERVAL = 3000; // 3 seconds

const ProcessingView = ({ jobId, columnMapping, onProcessingComplete, onError }: Props) => {
  const theme = useTheme();
  const classes = createClasses(processingViewStyles, theme);

  const [hasStarted, setHasStarted] = useState<boolean>(false);
  const [isPolling, setIsPolling] = useState<boolean>(false);
  const [importStatus, setImportStatus] = useState<ImportStatus | null>(null);

  const hasStartedRef = useRef<boolean>(false);

  const [processImport] = useProcessImportMutation();
  const { refetch: refetchStatus } = useGetImportStatusQuery(
    { jobId, user: auth.currentUser },
    { skip: !isPolling }
  );

  // Start processing on mount
  useEffect(() => {
    // Prevent double execution in React Strict Mode
    if (hasStartedRef.current) return;
    hasStartedRef.current = true;

    const startProcessing = async () => {
      const firebaseUser = auth.currentUser;
      if (!firebaseUser) {
        onError('Authentication session expired. Please log in again.');
        return;
      }

      try {
        await processImport({
          jobId,
          columnMapping,
          user: firebaseUser
        }).unwrap();

        setHasStarted(true);
        setIsPolling(true);
      } catch (error) {
        console.error('[ProcessingView] Start processing error:', error);
        const errorMessage = error.data?.message ||
          error.message ||
          'Failed to start processing. Please try again.';
        onError(errorMessage);
      }
    };

    startProcessing();
  }, [jobId, columnMapping, processImport, onError]);

  // Poll for status
  useInterval(
    async () => {
      if (!isPolling) return;

      const firebaseUser = auth.currentUser;
      if (!firebaseUser) {
        setIsPolling(false);
        onError('Authentication session expired. Please log in again.');
        return;
      }

      try {
        const result = await refetchStatus();
        if (result.data) {
          setImportStatus(result.data);

          // Check if processing is complete
          if (result.data.status === 'completed' || result.data.status === 'failed') {
            setIsPolling(false);

            // Small delay before transitioning to results
            setTimeout(() => {
              onProcessingComplete(result.data);
            }, 500);
          }
        }
      } catch (error) {
        console.error('[ProcessingView] Polling error:', error);
        // Don't stop polling on transient errors, just log them
      }
    },
    isPolling ? POLL_INTERVAL : null
  );

  const getProgress = (): number => {
    if (!importStatus) return 0;
    if (importStatus.totalRows === 0) return 0;
    return (importStatus.processedRows / importStatus.totalRows) * 100;
  };

  const getStatusText = (): string => {
    if (!hasStarted) return 'Starting import...';
    if (!importStatus) return 'Initializing...';
    if (importStatus.status === 'completed') return 'Import complete!';
    if (importStatus.status === 'failed') return 'Import failed';
    return `Processing ${formatNumber(importStatus.processedRows)} of ${formatNumber(importStatus.totalRows)} customers...`;
  };

  return (
    <Box sx={classes.container}>
      <Box sx={classes.content}>
        <Box sx={classes.header}>
          {importStatus?.status === 'completed' ? (
            <CheckCircleIcon sx={classes.successIcon} />
          ) : (
            <CircularProgress size={32} sx={classes.spinner} />
          )}
          <Typography variant="h6" sx={classes.title}>
            {importStatus?.status === 'completed' ? 'Import Complete!' : 'Importing Customers'}
          </Typography>
        </Box>

        <Box sx={classes.progressContainer}>
          <LinearProgress
            variant="determinate"
            value={getProgress()}
            sx={classes.progressBar}
          />
          <Box sx={classes.progressInfo}>
            <Typography variant="body2" sx={classes.statusText}>
              {getStatusText()}
            </Typography>
            <Typography variant="body2" sx={classes.progressPercent}>
              {Math.round(getProgress())}%
            </Typography>
          </Box>
        </Box>

        <Box sx={classes.counters}>
          <Box sx={classes.counter}>
            <Box sx={classes.counterIcon}>
              <PersonAddIcon sx={classes.createdIcon} />
            </Box>
            <Typography variant="h5" sx={classes.counterValue}>
              {formatNumber(importStatus?.created || 0)}
            </Typography>
            <Typography variant="caption" sx={classes.counterLabel}>
              Created
            </Typography>
          </Box>

          <Box sx={classes.counter}>
            <Box sx={classes.counterIcon}>
              <SkipNextIcon sx={classes.skippedIcon} />
            </Box>
            <Typography variant="h5" sx={classes.counterValue}>
              {formatNumber(importStatus?.skipped || 0)}
            </Typography>
            <Typography variant="caption" sx={classes.counterLabel}>
              Skipped
            </Typography>
          </Box>

          <Box sx={classes.counter}>
            <Box sx={classes.counterIcon}>
              <ErrorOutlineIcon sx={classes.failedIcon} />
            </Box>
            <Typography variant="h5" sx={classes.counterValue}>
              {formatNumber(importStatus?.failed || 0)}
            </Typography>
            <Typography variant="caption" sx={classes.counterLabel}>
              Failed
            </Typography>
          </Box>
        </Box>

        <Typography variant="body2" sx={classes.hint}>
          Please wait while we import your customers. This may take a few minutes for large files.
        </Typography>
      </Box>
    </Box>
  );
};

export default ProcessingView;
